import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const NewsletterSubscription = () => {
  const [email, setEmail] = useState('');
  const [preferences, setPreferences] = useState({
    weeklyInsights: true,
    marketReports: true,
    exclusiveContent: false,
    eventUpdates: false
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle newsletter subscription
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 3000);
  };

  const handlePreferenceChange = (key, checked) => {
    setPreferences(prev => ({
      ...prev,
      [key]: checked
    }));
  };

  const subscriptionBenefits = [
    {
      icon: "TrendingUp",
      title: "Weekly Market Intelligence",
      description: "Exclusive insights on GCC market trends and opportunities"
    },
    {
      icon: "FileText",
      title: "Premium Research Reports",
      description: "Early access to comprehensive industry analysis and data"
    },
    {
      icon: "Users",
      title: "Expert Commentary",
      description: "Direct insights from our advisory team and industry leaders"
    },
    {
      icon: "Calendar",
      title: "Event Invitations",
      description: "Priority access to webinars, workshops, and networking events"
    }
  ];

  if (isSubmitted) {
    return (
      <div className="bg-gradient-to-r from-success/10 to-success/20 rounded-xl p-8 text-center">
        <div className="w-16 h-16 bg-success rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Check" size={32} className="text-success-foreground" />
        </div>
        <h3 className="font-headline font-semibold text-xl text-success mb-2">
          Welcome to Bootminds Intelligence!
        </h3>
        <p className="text-success/80 mb-4">
          Thank you for subscribing. You'll receive your first market intelligence report within 24 hours.
        </p>
        <p className="text-sm text-success/70">
          Check your email for confirmation and subscription preferences.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-primary to-secondary rounded-xl overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Side - Benefits */}
        <div className="p-8 lg:p-12 text-primary-foreground">
          <div className="flex items-center mb-6">
            <Icon name="Mail" size={32} className="mr-4" />
            <div>
              <h2 className="font-headline font-bold text-2xl lg:text-3xl">
                Stay Ahead of the Market
              </h2>
              <p className="text-lg opacity-90 mt-2">
                Join 15,000+ executives receiving exclusive insights
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {subscriptionBenefits.map((benefit, index) => (
              <div key={index} className="flex items-start">
                <div className="w-10 h-10 bg-primary-foreground/20 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <Icon name={benefit.icon} size={20} />
                </div>
                <div>
                  <h3 className="font-medium text-lg mb-1">{benefit.title}</h3>
                  <p className="opacity-90 text-sm">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 pt-6 border-t border-primary-foreground/20">
            <div className="flex items-center space-x-6 text-sm">
              <div className="flex items-center">
                <Icon name="Users" size={16} className="mr-2" />
                15,000+ subscribers
              </div>
              <div className="flex items-center">
                <Icon name="Star" size={16} className="mr-2" />
                4.9/5 rating
              </div>
              <div className="flex items-center">
                <Icon name="Shield" size={16} className="mr-2" />
                GDPR compliant
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Subscription Form */}
        <div className="bg-card p-8 lg:p-12">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <h3 className="font-headline font-semibold text-xl text-text-primary mb-2">
                Get Exclusive Access
              </h3>
              <p className="text-text-secondary">
                Subscribe to receive premium content and market intelligence directly in your inbox.
              </p>
            </div>

            <Input
              type="email"
              label="Email Address"
              placeholder="your.email@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <div>
              <h4 className="font-medium text-text-primary mb-3">Content Preferences</h4>
              <div className="space-y-3">
                <Checkbox
                  label="Weekly Market Insights"
                  description="Curated analysis of GCC market trends and opportunities"
                  checked={preferences.weeklyInsights}
                  onChange={(e) => handlePreferenceChange('weeklyInsights', e.target.checked)}
                />
                <Checkbox
                  label="Quarterly Research Reports"
                  description="Comprehensive industry reports with data and forecasts"
                  checked={preferences.marketReports}
                  onChange={(e) => handlePreferenceChange('marketReports', e.target.checked)}
                />
                <Checkbox
                  label="Exclusive Content & Case Studies"
                  description="Premium resources not available to general public"
                  checked={preferences.exclusiveContent}
                  onChange={(e) => handlePreferenceChange('exclusiveContent', e.target.checked)}
                />
                <Checkbox
                  label="Event & Webinar Invitations"
                  description="Priority access to industry events and expert sessions"
                  checked={preferences.eventUpdates}
                  onChange={(e) => handlePreferenceChange('eventUpdates', e.target.checked)}
                />
              </div>
            </div>

            <Button
              type="submit"
              variant="default"
              size="lg"
              fullWidth
              className="bg-primary hover:bg-primary/90"
              iconName="ArrowRight"
              iconPosition="right"
            >
              Subscribe to Intelligence Hub
            </Button>

            <p className="text-xs text-text-secondary text-center">
              By subscribing, you agree to our{' '}
              <a href="/privacy-policy" className="text-primary hover:underline">Privacy Policy</a>
              <a href="/terms-of-service" className="text-primary hover:underline">Terms of Service</a>.
              Unsubscribe anytime.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewsletterSubscription;